import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgSrc = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;

const scrollToGrid = () => {
  if (window.__lenis) {
    window.__lenis.scrollTo("#gallery-grid", { duration: 1.2, offset: -72 });
  } else {
    document.getElementById("gallery-grid")?.scrollIntoView({ behavior: "smooth" });
  }
};

const Lightbox = ({ photos, index, onClose, onNav }) => {
  const touchX = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNav(-1);
      if (e.key === "ArrowRight") onNav(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const photo = photos[index];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0A2647]/92 backdrop-blur-sm"
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) onNav(dx > 0 ? -1 : 1);
        touchX.current = null;
      }}
      data-testid="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close photo viewer"
        className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white/80 transition-colors duration-300 hover:border-[#F47221] hover:text-[#F47221]"
      >
        <X className="h-5 w-5" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNav(-1);
            }}
            data-testid="lightbox-prev"
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/20 text-white/80 transition-colors duration-300 hover:border-[#F47221] hover:text-[#F47221] md:left-6"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNav(1);
            }}
            data-testid="lightbox-next"
            aria-label="Next photo"
            className="absolute right-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/20 text-white/80 transition-colors duration-300 hover:border-[#F47221] hover:text-[#F47221] md:right-6"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <img
        key={photo.id}
        src={imgSrc(photo.url)}
        alt="Event photo"
        data-testid="lightbox-image"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
      />
      <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs font-medium tracking-[0.2em] text-white/60">
        {index + 1} / {photos.length}
      </p>
    </div>
  );
};

export default function Gallery() {
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/gallery/years`)
      .then((r) => r.json())
      .then((data) => {
        setYears(data);
        if (data.length) setActiveYear(data[0].year);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const openCategory = async (year, name) => {
    setSelected({ year, name });
    setLoadingPhotos(true);
    try {
      const r = await fetch(
        `${API}/gallery/photos?year=${year}&category=${encodeURIComponent(name)}`
      );
      setPhotos(await r.json());
    } catch {
      setPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
    setTimeout(scrollToGrid, 120);
  };

  const navLightbox = useCallback(
    (dir) => {
      setLightbox((cur) =>
        cur === null ? cur : (cur + dir + photos.length) % photos.length
      );
    },
    [photos.length]
  );

  const activeYearData = years.find((y) => y.year === activeYear);

  return (
    <main className="relative">
      <Navbar />
      <PageHero
        eyebrow="Val-Hue High School & Junior College"
        title="Gallery"
        titleTestId="gallery-title"
        heroTestId="gallery-hero"
      />

      <section className="relative" data-testid="gallery-events-section">
        <div className="mx-auto max-w-6xl px-6 py-24 md:py-28">
          <Reveal>
            <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#F47221]">
              <span className="font-display text-2xl tracking-normal text-[#143A66]/25">
                01
              </span>
              Events
            </p>
            <h2 className="mt-5 font-display text-3xl font-semibold text-[#143A66] md:text-4xl">
              Event albums by year
            </h2>
          </Reveal>

          {loaded && years.length === 0 && (
            <p className="mt-12 text-sm text-[#143A66]/60" data-testid="gallery-empty">
              [Event photos will appear here once added by the school.]
            </p>
          )}

          {years.length > 0 && (
            <>
              <Reveal
                delay={0.08}
                className="mt-10 flex flex-wrap gap-2"
                data-testid="year-tabs"
              >
                {years.map((y) => (
                  <button
                    key={y.year}
                    type="button"
                    onClick={() => setActiveYear(y.year)}
                    data-testid={`year-tab-${y.year}`}
                    className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                      activeYear === y.year
                        ? "border-[#143A66] bg-[#143A66] text-white"
                        : "border-[#143A66]/20 bg-white/60 text-[#143A66]/70 hover:border-[#F47221] hover:text-[#F47221]"
                    }`}
                  >
                    {y.year}
                  </button>
                ))}
              </Reveal>

              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-cards">
                {activeYearData?.categories.map((cat, i) => (
                  <Reveal key={cat.name} delay={0.05 * i}>
                    <button
                      type="button"
                      onClick={() => openCategory(activeYear, cat.name)}
                      data-testid={`category-card-${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`group relative block w-full overflow-hidden rounded-2xl text-left ring-1 transition-shadow duration-500 hover:shadow-[0_24px_50px_-20px_rgba(10,38,71,0.4)] ${
                        selected?.year === activeYear && selected?.name === cat.name
                          ? "ring-2 ring-[#F47221]"
                          : "ring-[#143A66]/10"
                      }`}
                    >
                      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#0E2C4E]">
                        {cat.cover ? (
                          <img
                            src={imgSrc(cat.cover)}
                            alt={cat.name}
                            loading="lazy"
                            className="h-full w-full object-cover saturate-[0.8] transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.05] group-hover:saturate-100"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <span className="block h-14 w-14 overflow-hidden rounded-full opacity-40 ring-1 ring-white/20">
                              <img
                                src="/logo.png"
                                alt=""
                                className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
                              />
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A2647]/85 to-transparent px-5 pb-4 pt-10">
                          <p className="font-display text-lg font-semibold text-white">
                            {cat.name}
                          </p>
                          <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#D6EBEE]/70">
                            {cat.count > 0 ? `${cat.count} / 8 photos` : "Photos coming soon"}
                          </p>
                        </div>
                      </div>
                    </button>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section id="gallery-grid" className="relative scroll-mt-20" data-testid="gallery-grid-section">
        <div className="mx-auto max-w-6xl px-6 pb-24 md:pb-32">
          {selected ? (
            <>
              <Reveal>
                <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#F47221]">
                  <span className="font-display text-2xl tracking-normal text-[#143A66]/25">
                    02
                  </span>
                  {selected.year}
                </p>
                <h2
                  className="mt-5 font-display text-3xl font-semibold text-[#143A66] md:text-4xl"
                  data-testid="grid-heading"
                >
                  {selected.name}
                </h2>
              </Reveal>

              {loadingPhotos ? (
                <p className="mt-10 text-sm text-[#143A66]/60">Loading photos…</p>
              ) : photos.length === 0 ? (
                <p className="mt-10 text-sm text-[#143A66]/60" data-testid="grid-empty">
                  [No photos in this album yet.]
                </p>
              ) : (
                <div
                  className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4"
                  data-testid="photo-grid"
                >
                  {photos.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setLightbox(i)}
                      data-testid={`photo-thumb-${i}`}
                      className="group relative aspect-square overflow-hidden rounded-xl ring-1 ring-[#143A66]/10 transition-shadow duration-500 hover:shadow-[0_20px_40px_-18px_rgba(10,38,71,0.45)]"
                    >
                      <img
                        src={imgSrc(p.url)}
                        alt={`${selected.name} photo ${i + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-sm text-[#143A66]/50" data-testid="grid-hint">
              Choose an event above to view its photos.
            </p>
          )}
        </div>
      </section>

      <Footer />

      {lightbox !== null && photos.length > 0 && (
        <Lightbox
          photos={photos}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNav={navLightbox}
        />
      )}
    </main>
  );
}
